import React, { useState, useMemo, HTMLAttributes } from 'react';
import '../../common/utilities/Section.scss';
import * as DTO from '../../common/apis/dtos';
import { ValueOrDefault, IsoToDate, Stringable, LastKnownLocationOrDefault } from '../../common/utilities/display-utilities';
import { Button, Grid, Stack, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography } from '@mui/material';
import { useTranslation, toLocaleNumber } from '@chr/online-shared-ui-localizations';
import RiskLevelComponent from '../../common/components/riskLevel/RiskLevelComponent';
import { ToggleButtonText } from '../../common/constants/textOptions';


interface ContainerSummaryProps {
  containers: DTO.Container[];
  isMobileScreen: boolean;
}

export const ContainerSummary = (props: ContainerSummaryProps) => {
  const translate = useTranslation();
  const [showAllContainers, setShowAllContainers] = useState(false);
  const numberOfRowsToDisplay = 3;
  const summaries: InternalContainerSummary[] = props.containers.map(c => {
    return {
      estimatedDelivery: c.estimatedDelivery,
      requestedDelivery: c.requestedDelivery,
      delivered: c.delivered,
      lastKnownLocation: LastKnownLocationOrDefault(c.location?.city, c.location?.state, IsoToDate(c.eventDateTime)),
      number: c.containerNumber,
      riskLevel: c.riskLevel,
      status: c.status,
      type: c.containerType,
      translate: translate,
    } as InternalContainerSummary;
  }).filter(v => v);

  const footer = (
    <Typography className='section-footer' sx={{paddingY: '4px' }}>
      <Button
        variant='text'
        sx={{ fontSize: '12px', '&.ns-icon::before': { fontWeight: 100 } }}
        data-testid='container-summary-expand'
        aria-label={`${showAllContainers ? translate(ToggleButtonText.ViewLess) : translate(ToggleButtonText.ViewMore)} containers`}
        className={`ns-icon ${showAllContainers ? 'ns-collapse' : 'ns-expand'}`}
        onClick={() => setShowAllContainers(prev => !prev)}
      >
        {showAllContainers ? translate(ToggleButtonText.ViewLess) : translate(ToggleButtonText.ViewMore)}
      </Button>
    </Typography>
  );

  const viewProps: ContainerSummariesViewProps = {
    summaries: summaries,
    numberOfRowsToDisplay: numberOfRowsToDisplay,
    showAllContainers: showAllContainers,
    translate: translate,
    ...props,
  };

  return (
    <Stack className='section'>
      <Typography className='section-heading'>{translate('Container Summary')}</Typography>
      <div className='section-body' data-testid='container-summary'>
        {props.isMobileScreen ? <ContainerSummariesMobileView {...viewProps} /> : <ContainerSummariesDesktopView {...viewProps} />}
        {props.containers.length > numberOfRowsToDisplay && footer}
      </div>
    </Stack>
  );
};

interface InternalContainerSummary {
  estimatedDelivery: string;
  lastKnownLocation: string;
  requestedDelivery: string;
  delivered: string;
  number: string;
  riskLevel: DTO.RiskLevel;
  status: DTO.Status;
  type: string;
}

interface ContainerSummaryMobileViewProps extends InternalContainerSummary {
  numberOfRowsToDisplay: number;
  rowNumber: number;
  totalNumberOfRows: number;
  translate: (key: string, replaceValues?: Stringable[]) => string;
}

interface ContainerSummariesViewProps extends ContainerSummaryProps {
  summaries: InternalContainerSummary[];
  numberOfRowsToDisplay: number;
  showAllContainers: boolean;
  translate: (key: string, replaceValues?: Stringable[]) => string;
}

const ContainerSummariesDesktopView = ({ summaries, numberOfRowsToDisplay, showAllContainers, translate }: ContainerSummariesViewProps) => {

  const rows = summaries
    .filter((_, i) => i < numberOfRowsToDisplay || showAllContainers)
    .map((summary, i) => (
      <TableRow
        key={`${summary.number}_${i}`}
        data-testid='container-summary-row'
        sx={{ '& > td': {
          lineHeight: '24px !important',
          fontWeight: 400,
          fontSize: '14px',
          borderBottom: '1px solid #E5E6E7',
          padding: '14px 16px !important'
        }}}
      >
        <TableCell>{ValueOrDefault(summary.number)}</TableCell>
        <TableCell>{ValueOrDefault(summary.type)}</TableCell>
        <TableCell>{ValueOrDefault(summary.lastKnownLocation)}</TableCell>
        <TableCell>{IsoToDate(summary.requestedDelivery).toString()}</TableCell>
        <TableCell>{IsoToDate(summary.estimatedDelivery).toString()}</TableCell>
        <TableCell>{IsoToDate(summary.delivered).toString()}</TableCell>
        <TableCell>{translate(ValueOrDefault(summary.status))}</TableCell>
        <TableCell>{summary.status != DTO.Status.Delivered && <RiskLevelComponent data-testid='risk-level-component' riskLevel={summary.riskLevel} density='light' />}</TableCell>
        <TableCell>{translate('{0} of {1}', [toLocaleNumber(i + 1), toLocaleNumber(summaries.length)])}</TableCell>
      </TableRow>
    ));

  return (
    <TableContainer className='container-summary-desktop-view'>
        <Table className='table' sx={{ marginBottom: 0 }}>
          <TableHead data-testid='container-desktop-view-header'>
            <TableRow sx={{ '& > th': { lineHeight: '24px !important', fontWeight: 600, fontSize: '14px', borderBottom: '1px solid #AAAEB0 !important', padding: '14px 16px !important' }}}>
              <TableCell>{translate('Container Number')}</TableCell>
              <TableCell>{translate('Container Type')}</TableCell>
              <TableCell>{translate('Last Known Location')}</TableCell>
              <TableCell>{translate('Requested Delivery')}</TableCell>
              <TableCell>{translate('Estimated Delivery')}</TableCell>
              <TableCell>{translate('Delivered')}</TableCell>
              <TableCell>{translate('Status')}</TableCell>
              <TableCell />
              <TableCell />
            </TableRow>
          </TableHead>
          <TableBody key='table-body'>
            {rows}
          </TableBody>
        </Table>
      </TableContainer>
  );
};


interface ReadOnlyContentProps extends HTMLAttributes<HTMLDivElement> {  
  label?: string;
  text: string;
  iconElement?: React.ReactNode;
}

const MobileContent = ({ label, text, iconElement, ...htmlAttributes }: ReadOnlyContentProps) => {
  return (
    <Grid item xs={12} sx={{ marginBottom: 2 }}>
      <Stack direction='row' justifyContent='space-between' spacing={2} width='100%' {...htmlAttributes} >
        <Typography variant='body1' sx={{ alignSelf: 'center', lineHeight: '24px !important', fontWeight: 400, fontSize: '14px', }}>
          {label}
        </Typography>
        <Stack direction='column' justifyContent='flex-end' sx={{ '& > *': { lineHeight: '24px !important', fontWeight: 600 }}}>
          <Typography variant='subtitle2' className='text-right' sx={{ fontSize: '14px', }}>{text}</Typography>
          {iconElement}
        </Stack>
      </Stack>
    </Grid>
  );
}

const ContainerSummaryMobileView = (props: ContainerSummaryMobileViewProps) => {
  const [showMoreDetails, setShowMoreDetails] = useState(false);

  const displayMoreDetails = useMemo(() => (
    <Grid container sx={{ paddingTop: 2, borderTopWidth: '1px', borderTopStyle: 'solid', borderTopColor: 'divider' }}>
      <MobileContent label={props.translate('Container Type')} text={ValueOrDefault(props.type)} />
      <MobileContent label={props.translate('Last Known Location')} text={ValueOrDefault(props.lastKnownLocation)} />
      <MobileContent label={props.translate('Requested Delivery')} text={IsoToDate(props.requestedDelivery).toString()} />
      <MobileContent label={props.translate('Estimated Delivery')} text={IsoToDate(props.estimatedDelivery).toString()} />
      <MobileContent label={props.translate('Delivered')} text={IsoToDate(props.delivered).toString()} />
      <MobileContent label={props.translate('Status')} text={ValueOrDefault(props.status)} iconElement={<RiskLevelComponent riskLevel={props.riskLevel} density='light' size='small' />} />
      <Typography className='text-right' width='100%' sx={{ fontWeight: 400, fontSize: '12px', marginBottom: '16px' }}>{props.translate('{0} of {1}', [toLocaleNumber(props.rowNumber), toLocaleNumber(props.totalNumberOfRows)])}</Typography>
    </Grid>
  ), [props]);

  return (
    <Grid container sx={{ paddingX: 2, borderBottomWidth: '1px', borderBottomStyle: 'solid',  borderBottomColor: 'divider' }}>
      <Grid item xs={12} data-testid='container-summary-row'>
        <Stack direction='row' justifyContent='space-between' width='100%' spacing={2} sx={{ marginY: 2, '& > *': { lineHeight: '24px !important', fontWeight: 600, fontSize: '14px' } }}>
          <Stack direction='row' justifyContent='space-between' width='100%' spacing={2}>
            <Typography variant='subtitle2'>{props.translate('Container Number')}</Typography>
            <Typography variant='subtitle2' className='text-right'>{props.number}</Typography>
          </Stack>
          <Button
            variant='text'
            sx={{ fontSize: '12px', fontWeight: 100 }}
            data-testid='container-summary-expand-mobile'
            aria-label={`${showMoreDetails ? ToggleButtonText.ViewLess : ToggleButtonText.ViewMore} details for container ${props.number}`}
            className={`ns-icon ${showMoreDetails ? 'ns-collapse' : 'ns-expand'}`}
            onClick={() => setShowMoreDetails(!showMoreDetails)}
          />
        </Stack>
      </Grid>
      {showMoreDetails && displayMoreDetails}
    </Grid>
  );
};

const ContainerSummariesMobileView = (props: ContainerSummariesViewProps) => {

  const displayContainerSummaryRowsMobile = useMemo(() => props.summaries
    .filter((_, i) => i < props.numberOfRowsToDisplay || props.showAllContainers)
    .map((containerSummary, index) => (
      <ContainerSummaryMobileView
        key={index}
        rowNumber={index + 1}
        totalNumberOfRows={props.summaries.length}
        {...containerSummary}
        numberOfRowsToDisplay={props.numberOfRowsToDisplay}
        translate={props.translate}
      />
    )), [props]);

  return (
    <Grid container className='container-summaries-mobile-view'>
      {displayContainerSummaryRowsMobile}
    </Grid>
  );
};